import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/layout';
import { PageLoader } from './components/common';

// ─── Lazy pages ────────────────────────────────────────────
const Login          = lazy(() => import('./pages/Login'));
const CapexPage      = lazy(() => import('./pages/Capex'));
const Dashboard      = lazy(() => import('./pages/Dashboard'));
const AssetList      = lazy(() => import('./pages/Assets'));
const AssetDetail    = lazy(() => import('./pages/AssetDetail'));
const AssetGroup     = lazy(() => import('./pages/AssetGroup'));
const FARUpload      = lazy(() => import('./pages/FARUpload'));
const RFIDUpload     = lazy(() => import('./pages/RFIDUpload'));
const Audit          = lazy(() => import('./pages/Audit'));
const AuditResults   = lazy(() => import('./pages/AuditResults'));
const AuditRawDump   = lazy(() => import('./pages/AuditRawDump'));
const SuperAdmin     = lazy(() => import('./pages/SuperAdmin'));
const TagPrinting    = lazy(() => import('./pages/TagPrinting'));
const AllReports     = lazy(() => import('./pages/AllReports'));
const FieldReports   = lazy(() => import('./pages/FieldReports'));
const InsurancePage  = lazy(() => import('./pages/InsurancePage'));
const { ForgotPassword, CompanyHierarchy } = require('./pages/CompanyMaster');

// Eagerly loaded (used in multiple places)
const { AssetRegisterReport, ReconciliationReport, DepreciationReport: DepreciationRpt, TransferHistoryReport } = require('./pages/AllReports');
const { Transfers, Maintenance, DepreciationPage }  = require('./pages/Transactions');
const { TagMapping, Disposals, Checkouts, Custodians } = require('./pages/Misc');
const { Users, Locations, Categories, Vendors } = require('./pages/Masters');
const { Roles }  = require('./pages/Roles');

// ─── Guards ────────────────────────────────────────────────
const Protected = ({ children, title }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  return <Layout title={title}>{children}</Layout>;
};

const P = ({ component: Component, title }) => (
  <Protected title={title}>
    <Suspense fallback={<PageLoader />}><Component /></Suspense>
  </Protected>
);

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user)    return <Navigate to="/" replace />;
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right"
          toastOptions={{ duration:4000, style:{ fontSize:'13px', maxWidth:'400px', fontFamily:'Inter,system-ui,sans-serif' },
            success:{ iconTheme:{ primary:'#0F8A6E', secondary:'#fff' } },
            error:  { iconTheme:{ primary:'#C93030', secondary:'#fff' } } }} />
        <Routes>
          {/* Public */}
          <Route path="/login"          element={<PublicOnly><Suspense fallback={<PageLoader/>}><Login/></Suspense></PublicOnly>} />
          <Route path="/forgot-password"element={<Suspense fallback={<PageLoader/>}><ForgotPassword/></Suspense>} />
          <Route path="/superadmin"     element={<Suspense fallback={<PageLoader/>}><SuperAdmin/></Suspense>} />

          {/* Dashboard */}
          <Route path="/" element={<P component={Dashboard} title="Dashboard" />} />

          {/* Assets */}
          <Route path="/assets"               element={<P component={AssetList}   title="Asset Register"       />} />
          <Route path="/assets/group/:code"   element={<P component={AssetGroup}  title="Asset Group"          />} />
          <Route path="/assets/:id"           element={<P component={AssetDetail} title="Asset Detail"         />} />
          <Route path="/far-upload"           element={<P component={FARUpload}   title="FAR Upload"           />} />
          <Route path="/rfid-upload"          element={<P component={RFIDUpload}  title="RFID Upload"          />} />
          <Route path="/tag-mapping"          element={<P component={TagMapping}  title="Tag Mapping"          />} />
          <Route path="/tag-print"            element={<P component={TagPrinting} title="Tag Printing"         />} />

          {/* Transactions */}
          <Route path="/transfers"            element={<P component={Transfers}   title="Asset Transfers"      />} />
          <Route path="/disposals"            element={<P component={Disposals}   title="Scrap / Disposal"     />} />
          <Route path="/maintenance"          element={<P component={Maintenance} title="Asset Maintenance"    />} />
          <Route path="/checkouts"            element={<P component={Checkouts}   title="Check-In / Out"       />} />
          <Route path="/insurance"            element={<P component={InsurancePage} title="Insurance"          />} />

          {/* Audit & Finance */}
          <Route path="/audit"               element={<P component={Audit}            title="Physical Audit"   />} />
          <Route path="/audit/results/:sessionId" element={<P component={AuditResults} title="Audit Results"   />} />
          <Route path="/audit/raw-dump/:sessionId" element={<P component={AuditRawDump} title="Raw Data Dump"  />} />
          <Route path="/depreciation"        element={<P component={DepreciationPage} title="Depreciation"     />} />

          {/* Reports */}
          <Route path="/reports"                   element={<P component={AllReports}            title="Reports"                  />} />
          <Route path="/reports/asset-register"    element={<P component={AssetRegisterReport}   title="Asset Register Report"    />} />
          <Route path="/reports/reconciliation"    element={<P component={ReconciliationReport}  title="Reconciliation"           />} />
          <Route path="/reports/depreciation"      element={<P component={DepreciationRpt}       title="Depreciation Report"      />} />
          <Route path="/reports/transfers"         element={<P component={TransferHistoryReport} title="Transfer History"         />} />
          <Route path="/support"                   element={<P component={FieldReports}          title="Support"                  />} />

          {/* Masters */}
          <Route path="/masters/users"       element={<P component={Users}            title="User Management"     />} />
          <Route path="/masters/roles"       element={<P component={Roles}            title="Roles & Permissions" />} />
          <Route path="/masters/company"     element={<P component={CompanyHierarchy} title="Company Hierarchy"   />} />
          <Route path="/masters/locations"   element={<P component={Locations}        title="Location Master"     />} />
          <Route path="/masters/categories"  element={<P component={Categories}       title="Asset Categories"    />} />
          <Route path="/masters/vendors"     element={<P component={Vendors}          title="Vendor Master"       />} />
          <Route path="/masters/custodians"  element={<P component={Custodians}       title="Custodian Master"    />} />

          {/* CAPEX */}
          <Route path="/capex" element={<P component={CapexPage} title="CAPEX Management" />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
